// src/features/merchantConfig/state/EditStateContext.jsx
//
// Context that holds Phase-2 edit state for the currently-selected
// configuration category. Lives one level above ConfigDetailPanel and is
// keyed by configKey so switching categories naturally resets state.
//
// Shape (the public API of this context):
//   {
//     // — mode —
//     mode:        "view" | "edit",
//     // — original payload (read from TanStack Query cache) —
//     original:    Record<string, unknown> | null,   // value-block as received
//     // — working draft —
//     draft:       Record<string, unknown> | null,   // immutably updated on each edit
//     // — dirty / errors —
//     isDirty:     boolean,
//     dirtyKeys:   Set<string>,                       // pathToKey() of every changed leaf
//     fieldErrors: Map<string, string>,               // pathToKey() -> human msg
//     serverError: { error_code, error_message, error_context } | null,
//     // — save lifecycle —
//     isSaving:    boolean,
//     savedAt:     number | null,                     // Date.now() of last success
//     // — actions —
//     enterEditMode(): void,
//     cancelEdit(opts?: { skipPrompt?: boolean }): void,
//     updateField(path, newValue): void,
//     setFieldError(path, error | null): void,
//     setServerError(err | null): void,
//     setSavingFlag(saving: boolean): void,
//     resetAfterSave(envelope): void,                 // replaces original from server response
//   }
//
// All consumers go through `useEditState()` (throws if outside provider).
// Lightweight container components that need only `mode` can use
// `useEditMode()` to subscribe to just that slice (avoids re-renders on
// every keystroke).

import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useReducer,
  useRef,
} from "react";

import { setAtPath, pathToKey } from "../lib/jsonPath";
import { stripAuditFields } from "../lib/editFieldPermissions";

/* ---------- Reducer ---------- */

const INITIAL_STATE = {
  mode: "view",
  original: null,
  draft: null,
  dirtyKeys: new Set(),
  fieldErrors: new Map(),
  serverError: null,
  isSaving: false,
  savedAt: null,
};

function deepClone(v) {
  // structuredClone is available in all modern evergreen browsers + jsdom 16+.
  if (typeof structuredClone === "function") return structuredClone(v);
  // Fallback for older environments — JSON clone is sufficient for the
  // plain-JSON shape we receive from the OMS API.
  return JSON.parse(JSON.stringify(v ?? null));
}

function reducer(state, action) {
  switch (action.type) {
    case "SYNC_ORIGINAL": {
      // Server-side cache refresh while in view mode: just update `original`
      // (the renderer reads from `draft` only when mode === 'edit').
      if (state.mode !== "view") return state;
      return { ...state, original: action.payload };
    }
    case "ENTER_EDIT": {
      const cleanOriginal = stripAuditFields(action.payload || {});
      return {
        ...state,
        mode: "edit",
        original: cleanOriginal,
        draft: deepClone(cleanOriginal),
        dirtyKeys: new Set(),
        fieldErrors: new Map(),
        serverError: null,
        isSaving: false,
      };
    }
    case "CANCEL_EDIT": {
      return {
        ...state,
        mode: "view",
        draft: null,
        dirtyKeys: new Set(),
        fieldErrors: new Map(),
        serverError: null,
        isSaving: false,
      };
    }
    case "UPDATE_FIELD": {
      const { path, value } = action.payload;
      const key = pathToKey(path);
      const newDraft = setAtPath(state.draft ?? {}, path, value);
      const nextDirty = new Set(state.dirtyKeys);
      nextDirty.add(key);
      // Clear server error & field-level error on this path when user edits it.
      const nextFieldErrors = new Map(state.fieldErrors);
      nextFieldErrors.delete(key);
      return {
        ...state,
        draft: newDraft,
        dirtyKeys: nextDirty,
        fieldErrors: nextFieldErrors,
        serverError: null,
      };
    }
    case "SET_FIELD_ERROR": {
      const { path, error } = action.payload;
      const key = pathToKey(path);
      const next = new Map(state.fieldErrors);
      if (error) next.set(key, error);
      else next.delete(key);
      return { ...state, fieldErrors: next };
    }
    case "SET_SERVER_ERROR": {
      return { ...state, serverError: action.payload };
    }
    case "SET_SAVING": {
      return { ...state, isSaving: action.payload };
    }
    case "RESET_AFTER_SAVE": {
      const envelope = action.payload;
      // The PATCH response is the full envelope — we update `original` from
      // its value block, exit edit mode, and record the save timestamp.
      const valueField = action.valueField;
      const fresh =
        envelope && valueField && envelope[valueField]
          ? stripAuditFields(envelope[valueField])
          : null;
      return {
        ...state,
        mode: "view",
        original: fresh ?? state.original,
        draft: null,
        dirtyKeys: new Set(),
        fieldErrors: new Map(),
        serverError: null,
        isSaving: false,
        savedAt: Date.now(),
      };
    }
    default:
      return state;
  }
}

/* ---------- Context wiring ---------- */

const EditStateContext = createContext(null);

/**
 * @param {{ children: React.ReactNode, valueField?: string, initialOriginal?: Record<string, unknown> | null }} props
 */
export function EditStateProvider({ children, valueField, initialOriginal }) {
  const [state, dispatch] = useReducer(reducer, {
    ...INITIAL_STATE,
    original: initialOriginal ? stripAuditFields(initialOriginal) : null,
  });

  // Stash the latest valueField so RESET_AFTER_SAVE can pull the right slice
  // out of the PATCH response envelope without us having to thread it through
  // every dispatch site.
  const valueFieldRef = useRef(valueField);
  valueFieldRef.current = valueField;

  /* ---------- Stable action callbacks ---------- */

  const syncOriginal = useCallback((payload) => {
    dispatch({ type: "SYNC_ORIGINAL", payload });
  }, []);

  const enterEditMode = useCallback(
    (original) => {
      dispatch({ type: "ENTER_EDIT", payload: original ?? state.original });
    },
    [state.original]
  );

  const cancelEdit = useCallback(() => {
    dispatch({ type: "CANCEL_EDIT" });
  }, []);

  const updateField = useCallback((path, value) => {
    dispatch({ type: "UPDATE_FIELD", payload: { path, value } });
  }, []);

  const setFieldError = useCallback((path, error) => {
    dispatch({ type: "SET_FIELD_ERROR", payload: { path, error } });
  }, []);

  const setServerError = useCallback((err) => {
    dispatch({ type: "SET_SERVER_ERROR", payload: err });
  }, []);

  const setSavingFlag = useCallback((saving) => {
    dispatch({ type: "SET_SAVING", payload: Boolean(saving) });
  }, []);

  const resetAfterSave = useCallback((envelope) => {
    dispatch({
      type: "RESET_AFTER_SAVE",
      payload: envelope,
      valueField: valueFieldRef.current,
    });
  }, []);

  const value = useMemo(
    () => ({
      ...state,
      isDirty: state.dirtyKeys.size > 0,
      isEditing: state.mode === "edit",
      enterEditMode,
      cancelEdit,
      updateField,
      setFieldError,
      setServerError,
      setSavingFlag,
      syncOriginal,
      resetAfterSave,
    }),
    [
      state,
      enterEditMode,
      cancelEdit,
      updateField,
      setFieldError,
      setServerError,
      setSavingFlag,
      syncOriginal,
      resetAfterSave,
    ]
  );

  return (
    <EditStateContext.Provider value={value}>
      {children}
    </EditStateContext.Provider>
  );
}

/**
 * Read the full edit state. Throws if used outside an EditStateProvider —
 * encoder for "this component must live under the provider".
 */
export function useEditState() {
  const ctx = useContext(EditStateContext);
  if (!ctx) {
    throw new Error(
      "useEditState must be used inside an EditStateProvider. " +
        "Wrap the renderer at the page level."
    );
  }
  return ctx;
}

/**
 * Cheap selector hook for components that only need to know whether we are
 * in edit mode (e.g. ObjectSection header chrome). Subscribes to the full
 * context — React 18 ensures this is fine for boolean-only consumers since
 * the value object identity is memoised.
 */
export function useEditMode() {
  return useEditState().mode;
}
